/**
 * Typography System
 * Semantic typography scales and font tokens
 */

export interface TypographyVariant {
  fontSize: () => string;
  lineHeight: () => string;
  fontWeight: () => string;
  letterSpacing?: () => string;
}

export interface TypographyScale {
  display: {
    large: TypographyVariant;
    medium: TypographyVariant;
    small: TypographyVariant;
  };

  headline: {
    large: TypographyVariant;
    medium: TypographyVariant;
    small: TypographyVariant;
  };

  title: {
    large: TypographyVariant;
    medium: TypographyVariant;
    small: TypographyVariant;
  };

  body: {
    large: TypographyVariant;
    medium: TypographyVariant;
    small: TypographyVariant;
  };

  label: {
    large: TypographyVariant;
    medium: TypographyVariant;
    small: TypographyVariant;
  };

  caption: TypographyVariant;

  code: {
    large: TypographyVariant;
    medium: TypographyVariant;
    small: TypographyVariant;
  };
}

export interface FontFamily {
  primary: () => string; // Main text font
  secondary: () => string; // Headings font (if different)
  mono: () => string; // Monospace font
}

export interface FontWeight {
  thin: () => string; // 100
  light: () => string; // 300
  regular: () => string; // 400
  medium: () => string; // 500
  semibold: () => string; // 600
  bold: () => string; // 700
  extrabold: () => string; // 800
  black: () => string; // 900
}

export interface TypographySystem {
  scale: TypographyScale;
  fontFamily: FontFamily;
  fontWeight: FontWeight;
}

/**
 * Typography size scale used by Text and Heading primitives.
 *
 * Text components (Text10–Text18): regular weight (400), 1.2x line height
 * Heading components (Heading12–Heading22): bold weight (700), 1.2x line height
 */
export const Typography = {
  text: {
    10: { fontSize: "xs", lineHeight: "12.2px", fontWeight: 400 },
    11: { fontSize: "11px", lineHeight: "13.2px", fontWeight: 400 },
    12: { fontSize: "sm", lineHeight: "14.4px", fontWeight: 400 },
    14: { fontSize: "md", lineHeight: "16.8px", fontWeight: 400 },
    16: { fontSize: "lg", lineHeight: "19.2px", fontWeight: 400 },
    18: { fontSize: "18px", lineHeight: "21.6px", fontWeight: 400 },
  },
  heading: {
    12: { fontSize: "sm", lineHeight: "13px", fontWeight: 700 },
    14: { fontSize: "md", lineHeight: "16.8px", fontWeight: 700 },
    16: { fontSize: "lg", lineHeight: "19.2px", fontWeight: 700 },
    18: { fontSize: "18px", lineHeight: "21.6px", fontWeight: 700 },
    20: { fontSize: "xl", lineHeight: "24px", fontWeight: 700 },
    22: { fontSize: "22px", lineHeight: "26px", fontWeight: 600 },
  },
  weights: {
    light: 300,
    regular: 400,
    bold: 700,
    black: 900,
  },
} as const;
